import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'react-bootstrap';
import styled, { withTheme } from 'styled-components';
import Currency from '/util/Currency.js';

const StyledRow = styled(Row)`
  font-size: 15px;
  margin-bottom: 10px;
  display: flex;
  justify-content: center;
  cursor: pointer;
  user-select: none;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover .minus-amount:not(:hover) ~ .plus-amount:not(:disabled):not([disabled]) {
    background-color: rgb(37.6, 127.2, 37.6);
  }
`;

const ExtraText = styled.div`
  white-space: normal;
  word-break: break-word;
`;

const ExtraInfoWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;
const AmountButtonGroup = styled.div`
  margin-right: 3px;
  width: 83px;
  height: 20px;
  display: flex;
  border-radius: 6px;
  overflow: hidden;
  align-items: center;
`;

const AmountButton = styled.button`
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  width: 41px;
  height: 100%;
  line-height: 100%;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;

  &:disabled,
  &[disabled] {
    pointer-events: none;
    opacity: 0.75;
  }

  &:active,
  &[active] {
    background-color: ${({ theme }) => theme.colors.primary}!important;
  }

  &:hover {
    background-color: rgb(37.6, 127.2, 37.6);
  }

  &:first-child {
    border-radius: 0;
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
  }

  &:last-child {
    border-radius: 0;
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
  }
`;

const Divider = styled.div`
  width: 1px;
  background-color: white;
  height: 100%;
  align-self: center;
`;

const CountCircle = styled.div`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  background-color: ${({ selected, theme }) => (selected ? theme.colors.primary : 'transparent')};
  color: ${({ selected }) => (selected ? '#fff' : 'inherit')};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  margin-right: 8px;
  font-weight: 600;
  line-height: 1;
`;

const PriceDiv = styled.div`
  justify-content: flex-end;
  align-self: center;
  flex-grow: 1;
  text-align: right;
`;

function ExtrasManipulator({ extra, extraCharge, amountSelected, onIncrease, ...props }) {
  const [amount, setAmount] = useState(0);

  useEffect(() => {
    if (amountSelected > 0) {
      setAmount((prevState) => amountSelected);
    }
  }, [amountSelected]);

  const increaseAmount = (e, byValue = 1) => {
    if (e) e.stopPropagation();

    props.setLastChangedExtraId?.(extra._id);

    let newAmount;
    if (extra.limit === 1) {
      newAmount = amount === 1 ? 0 : 1;
    } else {
      newAmount = amount + byValue;
      if (extra.limit && newAmount > extra.limit) {
        return;
      }
    }

    setAmount(newAmount);
    onIncrease(extra, newAmount);
  };

  const decreaseAmount = (e) => {
    if (amount > 0) {
      setAmount((prevState) => prevState - 1);
      props.onDecrease(extra, amount - 1);
    }
    if (e) e.stopPropagation();
  };

  const displayCount = () => {
    if (extra.limit === 1 && amount > 0) return '✓';
    return amount > 0 ? amount : '';
  };

  return (
    <StyledRow key={extra._id} onClick={increaseAmount}>
      <Col xs={8} style={{ display: 'flex', alignItems: 'flex-start' }}>
        <ExtraInfoWrapper>
          <CountCircle selected={amount > 0}>{displayCount()}</CountCircle>

          <ExtraText>{extra.title}</ExtraText>
        </ExtraInfoWrapper>
      </Col>

      <Col
        xs={4}
        className="text-right"
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          fontWeight: 300,
          flexWrap: 'nowrap',
        }}
      >
        <PriceDiv style={{ display: 'inline-flex', justifyContent: 'flex-end' }}>
          {extra.limit !== 1 && props.lastChangedExtraId === extra._id && amount > 0 ? (
            <AmountButtonGroup>
              <AmountButton onClick={(event) => decreaseAmount(event)} className="minus-amount">
                －
              </AmountButton>
              <Divider />
              <AmountButton
                onClick={(event) => increaseAmount(event)}
                disabled={extra.limit === amount}
                className="plus-amount"
              >
                ＋
              </AmountButton>
            </AmountButtonGroup>
          ) : (
            <Currency as="span">
              {extra.protected ? extra.price ?? 0 : (extra.price ?? 0) + (extraCharge ?? 0)}
            </Currency>
          )}
        </PriceDiv>
      </Col>
    </StyledRow>
  );
}

ExtrasManipulator.propTypes = {
  extra: PropTypes.object.isRequired,
  theme: PropTypes.object,
  onIncrease: PropTypes.func.isRequired,
  onDecrease: PropTypes.func.isRequired,
  extraCharge: PropTypes.number,
  amountSelected: PropTypes.number,
  lastChangedExtraId: PropTypes.string,
  setLastChangedExtraId: PropTypes.func,
};

ExtrasManipulator.defaultValues = {
  extraCharge: 0,
  amountSelected: 0,
  lastChangedExtraId: null,
};

export default withTheme(ExtrasManipulator);
